import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/apiService';

export default function UserManagementModal({ isOpen, onClose }) {
    const { t } = useTranslation();
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    // 创建新用户状态
    const [isCreatingUser, setIsCreatingUser] = useState(false);
    const [newUserUsername, setNewUserUsername] = useState('');
    const [newUserPassword, setNewUserPassword] = useState('');
    const [newUserRole, setNewUserRole] = useState('');

    // 重命名状态
    const [renamingUserId, setRenamingUserId] = useState(null);
    const [newUsername, setNewUsername] = useState('');

    // 重置密码状态
    const [resettingPasswordUserId, setResettingPasswordUserId] = useState(null);
    const [newPassword, setNewPassword] = useState('');

    // 加载用户列表
    const fetchUsers = async () => {
        setIsLoading(true);
        setError('');
        try {
            const data = await apiService.getAllUsersForManagement();
            setUsers(data);
        } catch (err) {
            console.error('Failed to fetch users:', err);
            setError(err.message || t('userManagement.fetchUsersFailed'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchUsers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // 角色翻译映射
    const getRoleDisplay = (role) => {
        const roleMap = {
            'SuperAdmin': t('userManagement.roleSuperAdmin'),
            'Admin': t('userManagement.roleAdmin'),
            'Sales': t('userManagement.roleSales'),
            'Residence': t('userManagement.roleResidence'),
            'Accounting': t('userManagement.roleAccounting')
        };
        return roleMap[role] || role;
    };

    // 角色徽章颜色
    const getRoleBadgeColor = (role) => {
        const colorMap = {
            'SuperAdmin': 'bg-purple-100 text-purple-800',
            'Admin': 'bg-blue-100 text-blue-800',
            'Sales': 'bg-green-100 text-green-800',
            'Residence': 'bg-yellow-100 text-yellow-800',
            'Accounting': 'bg-pink-100 text-pink-800'
        };
        return colorMap[role] || 'bg-gray-100 text-gray-800';
    };

    // 开始重命名
    const handleStartRename = (user) => {
        setRenamingUserId(user.id);
        setNewUsername(user.username);
        setResettingPasswordUserId(null); // 关闭密码重置
    };

    // 提交重命名
    const handleSubmitRename = async (userId) => {
        if (!newUsername || newUsername.trim() === '') {
            alert(t('userManagement.usernameCannotBeEmpty'));
            return;
        }

        try {
            await apiService.updateUsername(userId, newUsername.trim());
            alert(t('userManagement.usernameUpdatedSuccess'));
            setRenamingUserId(null);
            setNewUsername('');
            fetchUsers(); // 刷新列表
        } catch (err) {
            alert(err.message || t('userManagement.usernameUpdateFailed'));
        }
    };

    // 名前変更をキャンセル
    const handleCancelRename = () => {
        setRenamingUserId(null);
        setNewUsername('');
    };

    // 开始重置密码
    const handleStartResetPassword = (user) => {
        setResettingPasswordUserId(user.id);
        setNewPassword('');
        setRenamingUserId(null); // 关闭重命名
    };

    // 提交重置密码
    const handleSubmitResetPassword = async (userId) => {
        if (!newPassword || newPassword.trim().length < 6) {
            alert(t('userManagement.passwordMinLength'));
            return;
        }

        const confirmReset = window.confirm(t('userManagement.confirmPasswordReset'));
        if (!confirmReset) return;

        try {
            await apiService.resetUserPassword(userId, newPassword.trim());
            alert(t('userManagement.passwordResetSuccess'));
            setResettingPasswordUserId(null);
            setNewPassword('');
        } catch (err) {
            alert(err.message || t('userManagement.passwordResetFailed'));
        }
    };

    // パスワードリセットをキャンセル
    const handleCancelResetPassword = () => {
        setResettingPasswordUserId(null);
        setNewPassword('');
    };

    // 删除用户
    const handleDeleteUser = async (user) => {
        const confirmDelete = window.confirm(
            t('userManagement.confirmDeleteUser', { username: user.username })
        );
        if (!confirmDelete) return;

        try {
            await apiService.deleteUserEnhanced(user.id);
            alert(t('userManagement.userDeletedSuccess', { username: user.username }));
            fetchUsers(); // 刷新列表
        } catch (err) {
            alert(err.message || t('userManagement.userDeleteFailed'));
        }
    };

    // 开始创建用户
    const handleStartCreateUser = () => {
        setIsCreatingUser(true);
        setNewUserUsername('');
        setNewUserPassword('');
        setNewUserRole('');
        setRenamingUserId(null); // 关闭其他编辑
        setResettingPasswordUserId(null); // 关闭其他编辑
    };

    // 提交创建用户
    const handleSubmitCreateUser = async () => {
        if (!newUserUsername || newUserUsername.trim() === '') {
            alert(t('userManagement.usernameCannotBeEmpty'));
            return;
        }
        if (!newUserPassword || newUserPassword.trim().length < 6) {
            alert(t('userManagement.passwordMinLength'));
            return;
        }
        if (!newUserRole) {
            alert(t('userManagement.roleCannotBeEmpty'));
            return;
        }

        try {
            await apiService.createUser(newUserUsername.trim(), newUserPassword.trim(), newUserRole);
            alert(t('userManagement.userCreatedSuccess', { username: newUserUsername.trim() }));
            setIsCreatingUser(false);
            setNewUserUsername('');
            setNewUserPassword('');
            setNewUserRole('');
            fetchUsers(); // 刷新列表
        } catch (err) {
            alert(err.message || t('userManagement.userCreateFailed'));
        }
    };

    // ユーザー作成をキャンセル
    const handleCancelCreateUser = () => {
        setIsCreatingUser(false);
        setNewUserUsername('');
        setNewUserPassword('');
        setNewUserRole('');
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="p-6 border-b flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-800">{t('userManagement.title')}</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto flex-1">
                    {isLoading && (
                        <p className="text-center py-8 text-gray-600">{t('loading')}</p>
                    )}

                    {error && (
                        <p className="text-center py-8 text-red-500">{error}</p>
                    )}

                    {!isLoading && !error && (
                        <>
                            {/* 新增用户按钮 */}
                            <div className="mb-4">
                                {!isCreatingUser ? (
                                    <button
                                        onClick={handleStartCreateUser}
                                        className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                                    >
                                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
                                        </svg>
                                        {t('userManagement.createNewUser')}
                                    </button>
                                ) : (
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-3">
                                            {t('userManagement.createUser')}
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                            <input
                                                type="text"
                                                value={newUserUsername}
                                                onChange={(e) => setNewUserUsername(e.target.value)}
                                                placeholder={t('userManagement.usernamePlaceholder')}
                                                className="border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-green-500"
                                                autoFocus
                                            />
                                            <input
                                                type="password"
                                                value={newUserPassword}
                                                onChange={(e) => setNewUserPassword(e.target.value)}
                                                placeholder={t('userManagement.passwordPlaceholder')}
                                                className="border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-green-500"
                                            />
                                            <select
                                                value={newUserRole}
                                                onChange={(e) => setNewUserRole(e.target.value)}
                                                className="border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-green-500"
                                            >
                                                <option value="">{t('userManagement.selectRole')}</option>
                                                <option value="SuperAdmin">{t('userManagement.roleSuperAdmin')}</option>
                                                <option value="Admin">{t('userManagement.roleAdmin')}</option>
                                                <option value="Sales">{t('userManagement.roleSales')}</option>
                                                <option value="Residence">{t('userManagement.roleResidence')}</option>
                                                <option value="Accounting">{t('userManagement.roleAccounting')}</option>
                                            </select>
                                        </div>
                                        <div className="flex space-x-2 mt-3">
                                            <button
                                                onClick={handleSubmitCreateUser}
                                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                                            >
                                                {t('userManagement.save')}
                                            </button>
                                            <button
                                                onClick={handleCancelCreateUser}
                                                className="px-4 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 text-sm"
                                            >
                                                {t('userManagement.cancel')}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('userManagement.username')}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('userManagement.role')}
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('userManagement.actions')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {users.map((user) => (
                                        <tr key={user.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {renamingUserId === user.id ? (
                                                    <div className="flex items-center space-x-2">
                                                        <input
                                                            type="text"
                                                            value={newUsername}
                                                            onChange={(e) => setNewUsername(e.target.value)}
                                                            className="border border-gray-300 rounded px-2 py-1 text-sm"
                                                            autoFocus
                                                        />
                                                        <button
                                                            onClick={() => handleSubmitRename(user.id)}
                                                            className="text-green-600 hover:text-green-800"
                                                            title={t('userManagement.save')}
                                                        >
                                                            ✓
                                                        </button>
                                                        <button
                                                            onClick={handleCancelRename}
                                                            className="text-red-600 hover:text-red-800"
                                                            title={t('userManagement.cancel')}
                                                        >
                                                            ✕
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {user.username}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                                                    {getRoleDisplay(user.role)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex justify-end space-x-2">
                                                    {/* 重命名按钮 */}
                                                    <button
                                                        onClick={() => handleStartRename(user)}
                                                        className="text-blue-600 hover:text-blue-900"
                                                        title={t('userManagement.rename')}
                                                    >
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                                                        </svg>
                                                    </button>

                                                    {/* 重置密码按钮/输入框 */}
                                                    {resettingPasswordUserId === user.id ? (
                                                        <div className="flex items-center space-x-2">
                                                            <input
                                                                type="password"
                                                                value={newPassword}
                                                                onChange={(e) => setNewPassword(e.target.value)}
                                                                placeholder={t('userManagement.newPasswordPlaceholder')}
                                                                className="border border-gray-300 rounded px-2 py-1 text-sm w-32"
                                                                autoFocus
                                                            />
                                                            <button
                                                                onClick={() => handleSubmitResetPassword(user.id)}
                                                                className="text-green-600 hover:text-green-800"
                                                                title={t('userManagement.save')}
                                                            >
                                                                ✓
                                                            </button>
                                                            <button
                                                                onClick={handleCancelResetPassword}
                                                                className="text-red-600 hover:text-red-800"
                                                                title={t('userManagement.cancel')}
                                                            >
                                                                ✕
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleStartResetPassword(user)}
                                                            className="text-yellow-600 hover:text-yellow-900"
                                                            title={t('userManagement.resetPassword')}
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"></path>
                                                            </svg>
                                                        </button>
                                                    )}

                                                    {/* 删除按钮 */}
                                                    <button
                                                        onClick={() => handleDeleteUser(user)}
                                                        className="text-red-600 hover:text-red-900"
                                                        title={t('userManagement.delete')}
                                                    >
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                                        </svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {users.length === 0 && !isLoading && !error && (
                                <p className="text-center py-8 text-gray-500">
                                    {t('userManagement.noUsersFound')}
                                </p>
                            )}
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-6 py-4 border-t flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                    >
                        {t('close')}
                    </button>
                </div>
            </div>
        </div>
    );
}
